<template>
	<component :is="svgComponent" />
</template>

<script setup lang="ts">
/**
 * Renders an SVG icon asynchronously with a hidden placeholder to prevent shifting during loading.
 * @prop {string} name - Name of the SVG icon file (without extension) from the assets/icons directory
 * @example
 * <BaseIcon name="search_v2" class="w-24px h-24px" />
 */
import { ref, watch, defineAsyncComponent, markRaw, onMounted } from 'vue'

const props = defineProps({
	name: {
		type: String,
		required: true,
	},
})

const svgComponent = ref(null)

const loadSvg = (svgName: string) => {
	if (!svgName) return

	const component = defineAsyncComponent({
		loader: () => import(`../../../assets/icons/${svgName}.svg?component`),
		loadingComponent: {
			template: '<span class="invisible" :class="$attrs.class" />',
		},
		delay: 0, // Set to 0 to make the invisible loading component appear immediately
	})

	svgComponent.value = markRaw(component)
}

onMounted(() => {
	loadSvg(props.name)
})

watch(
	() => props.name,
	newName => {
		loadSvg(newName)
	},
)
</script>
